import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
    FieldRegistryEntrySource,
    FieldRegistryExitMethod,
    FieldRegistryOrigin,
    FieldRegistryStep,
    FieldRegistryType,
    HarvestPrefill,
} from '/@/store/tracking/events/field-registry';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { ClaimProblem, ClaimType } from '/@/types/claim';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { isClaimValid } from '../claim-helpers';
import type { RegistryAddStep } from './types';

/**
 * One attempt at registering something in the field's seguimiento, as its Mixpanel events see it.
 * Whoever opens the flow starts it (`startFieldRegistryFlow`) and hands it down through
 * `FieldRegistryFlowContext`, so a form tags its events with the attempt it belongs to.
 */
export type FieldRegistryFlow = {
    flowId: string;
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    origin: FieldRegistryOrigin;
    entrySource: FieldRegistryEntrySource;
    /** The attempt this one was opened from: the visit report a claim is registered from, or the claim an application is added from. */
    parentFlowId?: string;
};

/** Unset outside a flow, e.g. editing a registry from its card: those forms send no funnel events. */
export const FieldRegistryFlowContext = createContext<FieldRegistryFlow | undefined>(undefined);

export const useFieldRegistryFlow = () => useContext(FieldRegistryFlowContext);

/** Read on unload too: requests are batched by default, and a queued event would miss it. */
const BEACON = { transport: 'sendBeacon', send_immediately: true } as const;

const activeCrop = (cropMonitoring?: CropMonitoring) =>
    cropMonitoring?.id && cropMonitoring.active ? cropMonitoring : undefined;

/** The properties every event of the attempt carries. */
export const flowEventProps = ({ flowId, fieldId, cropMonitoring, parentFlowId }: FieldRegistryFlow) => {
    const crop = activeCrop(cropMonitoring);
    return {
        flow_id: flowId,
        field_id: fieldId,
        crop: crop?.crop,
        phenology: crop?.cornCropMonitoring?.phenology,
        parent_flow_id: parentFlowId,
    };
};

/** The registry a step of "Agregar registro" stands for; the picker itself (or no step) is none. */
export const toRegistryType = (step: RegistryAddStep): FieldRegistryType | undefined => {
    if (step === 'select' || step === '') return undefined;
    return step === 'yieldEstimation' ? 'yield_estimation' : step;
};

/** Gives the attempt its id and reports it began. Call it from whatever opens the flow. */
export const startFieldRegistryFlow = (
    flow: Omit<FieldRegistryFlow, 'flowId'>,
    initialStep: FieldRegistryType | 'select'
): FieldRegistryFlow => {
    const started = { ...flow, flowId: uuidv4() };
    mpTrackEvent('Field Registry flow:Started', {
        ...flowEventProps(started),
        entry_source: flow.entrySource,
        initial_step: initialStep,
        has_active_campaign: Boolean(activeCrop(flow.cropMonitoring)),
    });
    return started;
};

export const trackFormOpened = (
    flow: FieldRegistryFlow,
    registryType: FieldRegistryType,
    extra: { step?: FieldRegistryStep; prefilled?: HarvestPrefill } = {}
) =>
    mpTrackEvent('Field Registry Form Opened', {
        ...flowEventProps(flow),
        registry_type: registryType,
        origin: flow.origin,
        ...extra,
    });

/** What the backend said, else its status, else the error itself. */
export const errorDetail = (error: unknown): string => {
    const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
    if (typeof response?.data?.detail === 'string') return response.data.detail;
    if (response?.status) return `HTTP ${response.status}`;
    return error instanceof Error ? error.message : String(error);
};

/**
 * Reports leaving a form without saving: through its own buttons (`end`), by navigating elsewhere
 * in the app (the form unmounts) or by closing or reloading the tab (`pagehide`). `finish` marks the
 * form as left for a reason that isn't an exit — saved, or back to the type picker — and silences
 * the rest. `report` is read when the exit happens, so it sees the form as the user left it.
 * A form that stays mounted while closed calls `restart` on each open, and `finish` when it closes.
 */
export const useFormExitTracking = (report: (exitMethod: FieldRegistryExitMethod) => void) => {
    const done = useRef(false);
    const reportRef = useRef(report);
    reportRef.current = report;

    const end = useCallback((exitMethod: FieldRegistryExitMethod) => {
        if (done.current) return;
        done.current = true;
        reportRef.current(exitMethod);
    }, []);
    const finish = useCallback(() => {
        done.current = true;
    }, []);
    const restart = useCallback(() => {
        done.current = false;
    }, []);

    const pendingUnmount = useRef<number | undefined>(undefined);
    useEffect(() => {
        // StrictMode remounts every effect once in development. Deferring the unmount report lets
        // that remount cancel it, instead of logging an exit the moment the form opens.
        window.clearTimeout(pendingUnmount.current);
        const onPageHide = () => end('page_exit');
        window.addEventListener('pagehide', onPageHide);
        return () => {
            window.removeEventListener('pagehide', onPageHide);
            pendingUnmount.current = window.setTimeout(() => end('navigation'));
        };
    }, [end]);

    return { end, finish, restart };
};

export const trackFormAbandoned = (
    flow: FieldRegistryFlow,
    registryType: FieldRegistryType,
    exitMethod: FieldRegistryExitMethod,
    formTouched: boolean,
    step?: FieldRegistryStep
) =>
    mpTrackEvent(
        'Field Registry Abandoned',
        {
            ...flowEventProps(flow),
            last_step: 'form',
            registry_type: registryType,
            exit_method: exitMethod,
            form_touched: formTouched,
            step,
        },
        BEACON
    );

/** Claims send the type, not the problem: that one is already stored with the claim. */
export const claimTypeProp = (type: ClaimType) => (type === 'SEEDS' ? 'seeds' : 'chemicals');

/** The fields that keep the claim from being registered, in the order the form shows them. */
export const claimInvalidFields = (type: ClaimType | '', problem: ClaimProblem | ''): Array<'type' | 'problem'> => {
    const invalid: Array<'type' | 'problem'> = [];
    if (!type) invalid.push('type');
    if (!isClaimValid(type, problem)) invalid.push('problem');
    return invalid;
};

/** Only a chemicals claim can point at the applications it is about. */
export const hasRelatedApplication = (type: ClaimType, applicationIds: number[]) =>
    type === 'CHEMICALS' && applicationIds.length > 0;

/** What the harvest form came with from FieldView: both dates, only one of them, or neither. */
export const harvestPrefill = ({
    plantingResultId,
    harvestResultId,
}: {
    plantingResultId?: unknown;
    harvestResultId?: unknown;
}): HarvestPrefill => {
    if (plantingResultId && harvestResultId) return 'both';
    return plantingResultId || harvestResultId ? 'partial' : 'none';
};

/** The harvest form's fields as the user sees them: a date picked from FieldView or typed is one field. */
const HARVEST_FIELDS: Record<string, string> = {
    plantingResultId: 'planting_date',
    plantingDate: 'planting_date',
    harvestResultId: 'harvest_date',
    harvestDate: 'harvest_date',
    yield: 'yield',
    humidity: 'humidity',
};

export const harvestInvalidFields = (fields: string[]) => [
    ...new Set(fields.map((field) => HARVEST_FIELDS[field] ?? field)),
];

/** The sample fields of the estimation, once each however many samples miss them. */
const ESTIMATION_FIELDS: Record<string, string> = {
    name: 'name',
    spikes: 'spikes',
    grainsSpike: 'grains_per_spike',
    weight1000Grains: 'weight_1000_grains',
};

export const estimationInvalidFields = (samples: Array<Record<string, unknown> | undefined> | undefined) => [
    ...new Set(
        (samples ?? []).flatMap((sample) => Object.keys(sample ?? {}).map((field) => ESTIMATION_FIELDS[field] ?? field))
    ),
];
